// Module patrimoine : types, libellés et petits composants partagés.
import type { ReactNode } from "react";
import { Badge, Input, StatusChip, type CheckStatus } from "@core/ui";
import { date } from "@core/format";

export type Address = { line1: string; line2: string; postal_code: string; city: string };
export const EMPTY_ADDRESS: Address = { line1: "", line2: "", postal_code: "", city: "" };

export type Occupation = { status: "vacant" | "occupe" | "inclus" | "partiel" | "reserve"; label: string; lease_id?: string; since?: string };

export type Source = { label: string; url: string; checked: string };

export type RegistryItem = {
  kind: string;
  kind_label: string;
  applicability: { code: "requis" | "a_disposition" | "non_requis" | "a_verifier"; reason: string };
  status: DiagStatus;
  status_label: string;
  valid_until: string | null;
  diagnostic_id: string | null;
  performed_on: string | null;
  has_document: boolean;
  message: string;
  source: Source;
};

export type DiagSummary = { items: RegistryItem[]; missing: number; expired: number; soon: number; to_check: number; next: RegistryItem | null; rules_version: string };

export type Property = {
  id: string;
  kind: string;
  kind_label: string;
  label: string;
  address: Address;
  address_line: string;
  building_id: string | null;
  parent_id: string | null;
  owner_id: string | null;
  surface_m2: number | null;
  rooms: number | null;
  furnished: boolean;
  dpe_class: string | null;
  zone_tendue: boolean;
  encadrement_loyers: boolean;
  permis_de_louer: boolean;
  details: Record<string, any>;
  created_at: string;
  archived_at: string | null;
  occupation?: Occupation;
  counted?: boolean;
  building_name?: string | null;
  parent_label?: string | null;
  owner_name?: string | null;
  owners_count?: number;
  children_count?: number;
  open_campaigns?: number;
  verification_status?: string;
  diagnostics?: DiagSummary | null;
};

export const KIND_OPTIONS = [
  { value: "logement", label: "Logement" },
  { value: "chambre", label: "Chambre (dans un logement)" },
  { value: "cave", label: "Cave" },
  { value: "parking", label: "Parking ou garage" },
  { value: "local_commercial", label: "Local commercial" },
  { value: "local_professionnel", label: "Local professionnel" },
  { value: "autre", label: "Autre dépendance" },
];

export const CHILD_KINDS = ["chambre", "cave", "parking", "autre"];

export const TRI_OPTIONS = [
  { value: "inconnu", label: "Je ne sais pas" },
  { value: "oui", label: "Oui" },
  { value: "non", label: "Non" },
];

export const CLASS_OPTIONS = [{ value: "", label: "Non renseignée" }, ...["A", "B", "C", "D", "E", "F", "G"].map((c) => ({ value: c, label: c }))];

export const OWNER_KINDS = [
  { value: "personne", label: "Personne" },
  { value: "sci", label: "SCI" },
  { value: "indivision", label: "Indivision" },
  { value: "societe", label: "Société" },
];

export type DiagStatus = "valide" | "bientot_echu" | "echu" | "sans_echeance" | "a_verifier" | "manquant" | "non_requis";

const DIAG_CHIP: Record<DiagStatus, CheckStatus> = {
  valide: "conforme",
  bientot_echu: "a_confirmer",
  echu: "anomalie",
  sans_echeance: "info",
  a_verifier: "a_confirmer",
  manquant: "manquant",
  non_requis: "info",
};

const DIAG_WORD: Record<DiagStatus, string> = {
  valide: "Valable",
  bientot_echu: "Échéance proche",
  echu: "Échu",
  sans_echeance: "Sans échéance",
  a_verifier: "À vérifier",
  manquant: "Pièce manquante",
  non_requis: "Non exigé",
};

/** Statut d'un diagnostic : icône + mot, jamais la couleur seule. */
export function DiagChip({ status, label }: { status: DiagStatus; label?: string }) {
  return <StatusChip status={DIAG_CHIP[status] ?? "info"} label={label ?? DIAG_WORD[status] ?? status} />;
}

const VERIF: Record<string, { chip: CheckStatus; word: string }> = {
  non_verifie: { chip: "info", word: "Non vérifié" },
  a_confirmer: { chip: "a_confirmer", word: "À confirmer : examen par l’équipe" },
  verifie_equipe: { chip: "conforme", word: "Vérifié par l’équipe Bailivo" },
  refuse: { chip: "anomalie", word: "Refusé" },
};

export function VerifChip({ status }: { status?: string }) {
  const v = VERIF[status ?? "non_verifie"] ?? VERIF.non_verifie;
  return <StatusChip status={v.chip} label={v.word} />;
}

export function OccupationBadge({ occupation }: { occupation?: Occupation }) {
  if (!occupation) return null;
  const tone = occupation.status === "vacant" ? "neutre" : occupation.status === "reserve" ? "attention" : occupation.status === "occupe" ? "succes" : "encre";
  return <Badge tone={tone}>{occupation.label}</Badge>;
}

export function SourceLinks({ sources }: { sources: Source[] }) {
  if (!sources?.length) return null;
  return (
    <div className="pat-sources">
      {sources.map((s) => (
        <a key={s.url + s.label} href={s.url} target="_blank" rel="noreferrer">
          {s.label}
          <span className="sr-only"> (s’ouvre dans un nouvel onglet)</span>
        </a>
      ))}
    </div>
  );
}

export function SourceLine({ source }: { source?: Source | null }) {
  if (!source) return null;
  return (
    <span className="pat-sub">
      Source :{" "}
      <a href={source.url} target="_blank" rel="noreferrer">
        {source.label}
      </a>{" "}
      · consultée le {date(source.checked)}
    </span>
  );
}

export function AddressFields({ value, onChange, errors = {}, required, help }: { value: Address; onChange: (a: Address) => void; errors?: Record<string, string | undefined>; required?: boolean; help?: ReactNode }) {
  return (
    <div className="stack-sm">
      <Input label="Adresse" required={required} value={value.line1} error={errors.line1} help={help} onChange={(e) => onChange({ ...value, line1: e.target.value })} autoComplete="street-address" />
      <Input label="Complément (étage, porte, bâtiment)" value={value.line2} onChange={(e) => onChange({ ...value, line2: e.target.value })} />
      <div className="form-row">
        <Input label="Code postal" required={required} inputMode="numeric" value={value.postal_code} error={errors.postal_code} onChange={(e) => onChange({ ...value, postal_code: e.target.value })} autoComplete="postal-code" />
        <Input label="Commune" required={required} value={value.city} error={errors.city} onChange={(e) => onChange({ ...value, city: e.target.value })} autoComplete="address-level2" />
      </div>
    </div>
  );
}

export { intOrNull, milliLabel, numOrNull } from "./calc";

export function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

export function yesNo(v: boolean | null | undefined): string {
  return v ? "Oui" : "Non";
}

export function triLabel(v: string | null | undefined): string {
  return v === "oui" ? "Oui" : v === "non" ? "Non" : "Non renseigné";
}
